import React, { CSSProperties, useContext, useEffect, useRef, useState } from 'react';
import { PageProps } from './interfaces';
import './styles.scss';

import Menu from '../../components/Menu';
import { useLocation } from 'react-router-dom';
import CustomLink from '../../components/CustomLink';
import { OverlayEnum, PageChildrenEnum } from '../../enums';
import Overlay from '../../components/Overlay';
import SspProviderContext from '../../utils/SspProvider/context';
import { SET_EXPOSE_ARROW_HIDDEN_ACTION, SET_VIDEO_STATE_ACTION } from '../../utils/SspProvider/store';
import { setOverlay } from '../../utils/SspProvider/dispatchFunctions';
import ASSubpage from '../../components/Overlay/AS-Subpages';

export default function Page(props: PageProps): JSX.Element {
    const store = useContext(SspProviderContext);

    const [menuOpen, setMenuOpen] = useState<boolean>(false);
    const [, setOverlayVisible] = useState<boolean>(false);
    const [yOffset, setYOffset] = useState<number>(0);
    const [pageStyle, setPageStyle] = useState<CSSProperties>({});
    const [show, setShow] = useState(true);
    const [lastScrollY, setLastScrollY] = useState(0);
    const scrollRef = useRef<HTMLIFrameElement>(null);

    const isExposeArrowHidden = store.state.exposeArrowHidden;
    const iFrameRef = useRef<HTMLIFrameElement>(null);

    const iFrameSrc = props.iframeSrc;

    /*** scrolls to the element with the id given by the #hash in the url ***/
    const location = useLocation();
    const pageHeaderRef = useRef<HTMLDivElement>(null);

    const overlayWithTransition =
        store.state.overlay && store.state.overlay.enum && !(store.state.overlay.enum === OverlayEnum.Loading);

    //hide header when scrolling down, show it when scrolling slightly up. hide it when subpages or overlays are open
    useEffect(() => {
        const scrollContainer = scrollRef.current;
        if (!scrollContainer) return;

        if (store.state.asSubpageStack.length > 0 || store.state.overlay?.enum !== undefined) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setShow(false);
            return;
        }

        const handleScroll = () => {
            const currentScrollY = scrollContainer.scrollTop;

            if (currentScrollY > lastScrollY && currentScrollY > 50) {
                setShow(false);
            } else {
                setShow(true);
            }

            setLastScrollY(currentScrollY);
        };

        scrollContainer.addEventListener('scroll', handleScroll);
        return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }, [lastScrollY, store.state.asSubpageStack.length, store.state.overlay?.enum]);

    useEffect(() => {
        // if not a hash link, scroll to top
        if (location.hash === '') {
            window.scrollTo(0, 0);
        }
        // else scroll to id
        else {
            setTimeout(() => {
                const id = location.hash.replace('#', '');
                const element = document.getElementById(id);
                if (element && pageHeaderRef.current) {
                    let position = element.offsetTop - pageHeaderRef.current.offsetHeight;
                    if (id === 'checkin') {
                        position = position + 50;
                    }
                    window.scrollTo({ top: position, behavior: 'smooth' });
                }
            }, 0);
        }
    }, [location]);

    useEffect(() => {
        // remove exposé arrow from DOM after 5s
        if (!isExposeArrowHidden) {
            const timer = setTimeout(() => {
                store.dispatch({ type: SET_EXPOSE_ARROW_HIDDEN_ACTION, value: !isExposeArrowHidden });
            }, 5000);
            return () => clearTimeout(timer);
        }
    });

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setOverlayVisible((prevState: boolean) => {
            const opened = store.state.overlay?.enum === OverlayEnum.ExternalContent && !!store.state.overlay;
            if (opened) {
                setYOffset(window.pageYOffset);
                setPageStyle({
                    overflowY: 'hidden',
                    position: 'fixed',
                    top: -window.scrollY + 'px',
                });
            }
            if (prevState && !store.state.overlay) {
                setPageStyle({});
            }
            return opened;
        });
    }, [store.state.overlay]);

    useEffect(() => {
        if (!pageStyle.top) {
            window.scrollTo({ top: yOffset });
        }
    }, [pageStyle, yOffset]);

    return (
        <div className={`page`}>
            {store.state.content && (
                <div
                    className='page-content'
                    ref={scrollRef}
                    style={props.child === PageChildrenEnum.Map ? { ...pageStyle, paddingBottom: 'unset' } : pageStyle}
                >
                    {props.children}
                </div>
            )}
            {iFrameSrc && iFrameSrc !== '' && (
                <>
                    <iframe
                        className='iframe'
                        ref={iFrameRef}
                        src={iFrameSrc}
                        title={'Hier wird die Webseite der Autostadt eingebunden.'}
                    />
                    {props.closeIframe && (
                        <button className='close-iframe-button' onClick={() => props.closeIframe()}>
                            <svg>
                                <use xlinkHref='#close' />
                            </svg>
                        </button>
                    )}
                </>
            )}
            {!props.iframeSrc && (
                <div className={`page-header ${show ? '--visible' : '--hidden'}`} ref={pageHeaderRef}>
                    <svg className='icon-24 header-burger' onClick={() => setMenuOpen(!menuOpen)}>
                        <use xlinkHref='#menu-burger' />
                    </svg>

                    <svg className='header-title'>
                        <use xlinkHref='#autostadt' />
                    </svg>
                </div>
            )}

            <div className={'page-footer'}>
                {props.child === PageChildrenEnum.Back && (
                    <span className='back-container' onClick={() => window.history.back()}>
                        <svg className='back-arrow'>
                            <use xlinkHref='#back-arrow' />
                        </svg>
                        <span className='back-text'>Zurück</span>
                    </span>
                )}
                {props.child !== PageChildrenEnum.Back && (
                    <>
                        <CustomLink to='/'>
                            <div
                                className={`footer-item${
                                    props.child === PageChildrenEnum.Expose ? ' active-item' : ''
                                }`}
                                aria-label='Mein Fahrzeug'
                                onClick={() => store.dispatch({ type: 'RESET_AS_SUBPAGE' })}
                            >
                                <svg className='icon-24'>
                                    <use xlinkHref='#car' />
                                </svg>
                            </div>
                        </CustomLink>
                        {props.editorialContent && (
                            <CustomLink to='/pick-up'>
                                <div
                                    className={`footer-item${
                                        props.child === PageChildrenEnum.PickUp ? ' active-item' : ''
                                    }`}
                                    aria-label='Meine Erlebnisabholung'
                                    onClick={() => store.dispatch({ type: 'RESET_AS_SUBPAGE' })}
                                >
                                    <svg className='icon-24'>
                                        <use xlinkHref='#story' />
                                    </svg>
                                </div>
                            </CustomLink>
                        )}

                        <CustomLink to='/story'>
                            <div
                                className={`footer-item${props.child === PageChildrenEnum.Story ? ' active-item' : ''}`}
                                aria-label='Volkswagen'
                                onClick={() => store.dispatch({ type: 'RESET_AS_SUBPAGE' })}
                            >
                                <svg className='icon-24'>
                                    <use xlinkHref='#home' />
                                </svg>
                            </div>
                        </CustomLink>
                    </>
                )}
            </div>

            {store.state.overlay &&
                store.state.overlay.enum !== OverlayEnum.Loading &&
                !overlayWithTransition &&
                !iFrameSrc && (
                    <div
                        className={`page-overlay-container overlay-open`}
                        onClick={() => {
                            setOverlay(store, null);
                            store.dispatch({ type: SET_VIDEO_STATE_ACTION, value: null });
                        }}
                        id='without-transition'
                    >
                        <meta name='theme-color' content='#333333' />
                        <Overlay />
                    </div>
                )}

            <div>
                <ASSubpage />
            </div>

            {!iFrameSrc && (
                <div className={`page-overlay-container transition${overlayWithTransition ? ' overlay-open' : ''}`}>
                    <Overlay />
                </div>
            )}
            <div className={`page-menu-container${menuOpen ? ' menu-open' : ''}`}>
                <Menu setMenuOpen={setMenuOpen} editorialContent={props.editorialContent} activePage={props.child} />
            </div>
        </div>
    );
}
